import { nextTick, onActivated, onDeactivated, onMounted, ref } from 'vue';
import type { AnyFunction } from '@mts-fe/types';
import { recursiveTimeout } from '@mts-fe/utils';

/**
 * 在 OnMounted 或者 OnActivated 时触发
 * @param hook 任何函数（包括异步函数）
 */
function onMountedOrActivated(hook: AnyFunction) {
  let mounted: boolean;

  onMounted(() => {
    hook();
    nextTick(() => {
      mounted = true;
    });
  });

  onActivated(() => {
    if (mounted) {
      hook();
    }
  });
}

export { onMountedOrActivated };

export function useComponentStatus() {
  const isActive = ref(true);

  onActivated(() => {
    isActive.value = true;
  });

  onDeactivated(() => {
    isActive.value = false;
  });

  return { isActive };
}

/**
 * KeepAlive 感知的轮询 Hook
 * - 组件挂载时立即启动轮询
 * - 页面隐藏（document.hidden）时自动跳过执行
 * - KeepAlive 缓存失活时自动停止，重新激活时自动重启
 * - 无 KeepAlive 时行为与直接调用 recursiveTimeout 一致
 *
 * @param fn - 轮询执行函数
 * @param interval - 轮询间隔（毫秒）
 * @param options - 可选配置
 * @param options.immediate - 是否立即执行首次（默认 false，等待 interval 后首次执行）
 * @param options.args - 传递给 fn 的参数
 * @param options.onError - 错误回调
 */
export function useKeepAlivePolling<T extends any[]>(
  fn: (...args: T) => boolean | void | Promise<boolean | void>,
  interval: number,
  options?: {
    immediate?: boolean;
    args?: T;
    onError?: (err: Error) => void;
  },
) {
  let timer: ReturnType<typeof recursiveTimeout> | undefined;

  const start = () => {
    timer?.stop();
    timer = recursiveTimeout(
      (...args: T) => {
        if (document.hidden) return;
        return fn(...args);
      },
      interval,
      options,
    );
  };

  start();

  onActivated(() => start());
  onDeactivated(() => timer?.stop());
}
